import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { SERVER_URL } from '../config';
import TableHeaderFilter from './TableHeaderfilter';
import SearchableDropdown from './SearchableDropdown';
import DateSearch from './DateSearch';
import UniversalSearchBar from './Universalsearchbar';
import { STATUS_DROPDOWN_OPTIONS } from '../config/statusConfig';

const api = axios.create({
    baseURL: SERVER_URL,
    headers: { 'Content-Type': 'application/json' },
    timeout: 10000,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v) => v || '—';
const fmtAmt = (v) =>
    v != null && v !== '' && Number(v) !== 0
        ? '₹' + Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })
        : '—';
const norm = (s) => (s || '').toString().trim().toLowerCase();
// Truncates an ISO-ish date/datetime string to a fixed length so it can be used
// both as a display value AND as a stable filter key (16 = 'YYYY-MM-DD HH:MM',
// 10 = 'YYYY-MM-DD').
const dateKey = (v, len = 16) => (v ? String(v).slice(0, len) : null);

const isToday = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
    );
};

const getLoggedInEmployee = () => {
    try {
        const raw = localStorage.getItem('employee');
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
};

const STATUS_STYLES = {
    open: 'bg-blue-50 text-blue-700 border border-blue-200',
    closed: 'bg-green-50 text-green-700 border border-green-200',
    pending: 'bg-amber-50 text-amber-700 border border-amber-200',
};
const JOB_STYLES = {
    complete: 'bg-green-50 text-green-700 border border-green-200',
    onprogress: 'bg-blue-50 text-blue-700 border border-blue-200',
    pending: 'bg-amber-50 text-amber-700 border border-amber-200',
    cancel: 'bg-rose-50 text-rose-700 border border-rose-200',
    'n/r': 'bg-gray-100 text-gray-500 border border-gray-200',
};
const BILLING_STYLES = {
    'billed&submitted': 'bg-purple-50 text-purple-700 border border-purple-200',
    'complete&billed': 'bg-green-50 text-green-700 border border-green-200',
    completebutnotbilled: 'bg-amber-50 text-amber-700 border border-amber-200',
    freeservice: 'bg-teal-50 text-teal-700 border border-teal-200',
    cancelledjob: 'bg-rose-50 text-rose-700 border border-rose-200',
    onprocess: 'bg-blue-50 text-blue-700 border border-blue-200',
    pending: 'bg-amber-50 text-amber-700 border border-amber-200',
    'n/r': 'bg-gray-100 text-gray-500 border border-gray-200',
};
const ATTEND_STYLES = {
    attend: 'bg-green-50 text-green-700 border border-green-200',
    notattend: 'bg-rose-50 text-rose-700 border border-rose-200',
};
const JOB_REPORT_STYLES = {
    complete: 'bg-green-50 text-green-700 border border-green-200',
    onprogress: 'bg-blue-50 text-blue-700 border border-blue-200',
    'n/r': 'bg-gray-100 text-gray-500 border border-gray-200',
};
const RECEIPT_STYLES = {
    done: 'bg-green-50 text-green-700 border border-green-200',
    'n/a': 'bg-gray-100 text-gray-500 border border-gray-200',
};
const VERIFIED_STYLES = {
    verified: 'bg-green-50 text-green-700 border border-green-200',
    not: 'bg-rose-50 text-rose-700 border border-rose-200',
};
const PRIORITY_STYLES = {
    mostpriority: 'bg-red-50 text-red-700 border border-red-200',
    highpriority: 'bg-orange-50 text-orange-700 border border-orange-200',
    mediumpriority: 'bg-amber-50 text-amber-700 border border-amber-200',
    lowpriority: 'bg-gray-100 text-gray-500 border border-gray-200',
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const Badge = ({ label, styleMap }) => {
    const key = (label || '').toLowerCase().replace(/\s/g, '');
    const cls = styleMap[key] || styleMap[label] || 'bg-gray-100 text-gray-600 border border-gray-200';
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide ${cls}`}>
            {label || '—'}
        </span>
    );
};

const Field = ({ label, children }) => (
    <div className="flex flex-col gap-1">
        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</label>
        {children}
    </div>
);

const inputCls =
    'w-full px-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-white text-slate-800 ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition placeholder-slate-300';

const SkeletonRows = ({ cols, rows = 8 }) =>
    Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="border-b border-slate-100">
            {Array.from({ length: cols }).map((__, j) => (
                <td key={j} className="px-3 py-2.5">
                    <div className="h-3 rounded bg-slate-100 animate-pulse" style={{ width: j === 0 ? '28px' : '75%' }} />
                </td>
            ))}
        </tr>
    ));

// ─── Empty form state ─────────────────────────────────────────────────────────
// `callStatus` uses the SAME status list as Leads and Follow-Ups.
// `verified` and `director` are NOT here — they are status flags flipped only
// by the Verify buttons.
const EMPTY_FORM = {
    callDate: '', partyName: '', partyAddress: '', callDetails: '',
    remarks: '', finalReview: '', priorityCustomer: 'Medium Priority',
    callStatus: '',
    scheduleDate: '', scheduleTechnician: '', tentativeCompletionDate: '',
    attendStatus: 'Attend',
    jobStatus: 'On Progress', dnDate: '', deliveryNoteNo: '',
    receiptNote: 'N/A', supervisedBy: '', billingStatus: 'Pending',
    jobReportRemarks: 'N/R', serviceChargeAmount: '', receivedAmount: '',
    serviceChargePaid: '', netAmountReceived: '', verifiedBy: '',
};

// ═════════════════════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════════════════════
export default function CallRegister({ onClose }) {

    const loggedInEmployee = useMemo(() => getLoggedInEmployee(), []);
    const myName = loggedInEmployee?.employee_name || loggedInEmployee?.name || '';
    const myRole = norm(loggedInEmployee?.job_role || loggedInEmployee?.department);

    // ── Role-based access ───────────────────────────────────────────────────
    // Admin, CRM and Director see every call history for every user.
    // Admin and Director additionally get the "Director Verify" action.
    const isAdmin = myRole === 'admin';
    const isDirector = myRole === 'director';
    const isCRM = myRole === 'crm';
    const canSeeAll = isAdmin || isDirector || isCRM;
    const canDirectorVerify = isAdmin || isDirector;

    // raw flat list — every single row from backend
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // employees
    const [allEmployees, setAllEmployees] = useState([]);
    const [technicians, setTechnicians] = useState([]);
    const [employeesLoading, setEmployeesLoading] = useState(false);

    // ── Main-table filters ────────────────────────────────────────────────────
    const [filterCallNumber, setFilterCallNumber] = useState([]);
    const [filterPartyName, setFilterPartyName] = useState([]);
    const [filterCallDate, setFilterCallDate] = useState([]);
    const [filterScheduleDate, setFilterScheduleDate] = useState([]);
    const [filterTentativeDate, setFilterTentativeDate] = useState([]);
    const [filterTechnician, setFilterTechnician] = useState([]);
    const [filterJob, setFilterJob] = useState([]);
    const [filterBilling, setFilterBilling] = useState([]);
    const [filterPriority, setFilterPriority] = useState([]);
    const [filterSupervisedBy, setFilterSupervisedBy] = useState([]);
    const [filterVerifiedBy, setFilterVerifiedBy] = useState([]);
    const [filterVerified, setFilterVerified] = useState([]);
    const [filterDirector, setFilterDirector] = useState([]);

    // ── Modal state ───────────────────────────────────────────────────────────
    const [selectedCallNumber, setSelectedCallNumber] = useState(null);
    const [relatedRecords, setRelatedRecords] = useState([]);
    const [relatedLoading, setRelatedLoading] = useState(false);
    const [modalFilterJob, setModalFilterJob] = useState([]);
    const [modalFilterBilling, setModalFilterBilling] = useState([]);
    const [verifying, setVerifying] = useState(false);

    // ── Form ──────────────────────────────────────────────────────────────────
    const [showAddForm, setShowAddForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState(null);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [rangeLoading, setRangeLoading] = useState(false);

    // ── Fetch employees ───────────────────────────────────────────────────────
    const loadEmployees = useCallback(async () => {
        setEmployeesLoading(true);
        try {
            const { data } = await api.get('/Rahul/get_employee.php');
            if (data.success && Array.isArray(data.data)) {
                const emps = data.data;
                setAllEmployees(emps.map((e) => ({ label: e.name, value: e.name, id: e.id })));
                setTechnicians(
                    emps
                        .filter((e) => {
                            const jobRole = norm(e.job_role);
                            const department = norm(e.department);
                            return jobRole === 'technician' || department === 'technician';
                        })
                        .map((e) => ({ label: e.name, value: e.name, id: e.id }))
                );
            }
        } catch { console.warn('Could not load employees.'); }
        finally { setEmployeesLoading(false); }
    }, []);

    // ── Fetch all call register rows ──────────────────────────────────────────
    const loadAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get('/Rahul/call_register.php');
            if (data.success) setRecords(data.data);
            else throw new Error(data.message);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load records.');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { loadAll(); loadEmployees(); }, [loadAll, loadEmployees]);

    // ─────────────────────────────────────────────────────────────────────────
    // GROUP records by callNumber.
    // Each group → { callNumber, latest, all, count }
    // Main table shows ONE row per group using latest entry data.
    // ─────────────────────────────────────────────────────────────────────────
    const groupedByCallNumber = useMemo(() => {
        const map = new Map();
        for (const r of records) {
            const cn = r.callNumber;
            if (!map.has(cn)) map.set(cn, []);
            map.get(cn).push(r);
        }
        const groups = [];
        map.forEach((rows, callNumber) => {
            const sorted = [...rows].sort(
                (a, b) => new Date(b.callDate || 0) - new Date(a.callDate || 0)
            );
            groups.push({ callNumber, latest: sorted[0], all: sorted, count: sorted.length });
        });
        groups.sort((a, b) => {
            const aToday = isToday(a.latest.callDate) ? 1 : 0;
            const bToday = isToday(b.latest.callDate) ? 1 : 0;
            if (bToday !== aToday) return bToday - aToday;
            return new Date(b.latest.callDate || 0) - new Date(a.latest.callDate || 0);
        });
        return groups;
    }, [records]);

    // ── Visibility (role-based) ─────────────────────────────────────────────
    // Admin / CRM / Director → everything.
    // Everyone else → only call numbers where THEY are the assigned technician
    // on some entry, or THEY are the person assigned to verify it.
    const visibleByRole = useMemo(() => {
        if (canSeeAll || !myName) return groupedByCallNumber;
        const mine = norm(myName);
        return groupedByCallNumber.filter((g) =>
            g.all.some((r) =>
                norm(r.scheduleTechnician).includes(mine) || norm(r.verifiedBy) === mine
            )
        );
    }, [groupedByCallNumber, canSeeAll, myName]);

    // ── Build filter option lists ─────────────────────────────────────────────
    const makeOptsFromLatest = (groups, key) => {
        const unique = [...new Set(groups.map((g) => g.latest[key]).filter(Boolean))];
        return unique.map((v) => ({ label: v, value: v }));
    };
    const makeOptsFromAll = (groups, key) => {
        const unique = [...new Set(groups.flatMap((g) => g.all.map((r) => r[key])).filter(Boolean))];
        return unique.map((v) => ({ label: v, value: v }));
    };
    const makeDateOptsFromLatest = (groups, key, len = 16) => {
        const unique = [...new Set(groups.map((g) => dateKey(g.latest[key], len)).filter(Boolean))];
        return unique.sort().reverse().map((v) => ({ label: v, value: v }));
    };

    const callNumberOpts = useMemo(() => visibleByRole.map((g) => ({ label: g.callNumber, value: g.callNumber })), [visibleByRole]);
    const partyNameOpts = useMemo(() => makeOptsFromAll(visibleByRole, 'partyName'), [visibleByRole]);
    const callDateOpts = useMemo(() => makeDateOptsFromLatest(visibleByRole, 'callDate', 16), [visibleByRole]);
    const scheduleDateOpts = useMemo(() => makeDateOptsFromLatest(visibleByRole, 'scheduleDate', 16), [visibleByRole]);
    const tentativeDateOpts = useMemo(() => makeDateOptsFromLatest(visibleByRole, 'tentativeCompletionDate', 10), [visibleByRole]);
    const technicianOpts = useMemo(() => makeOptsFromLatest(visibleByRole, 'scheduleTechnician'), [visibleByRole]);
    const jobOpts = useMemo(() => makeOptsFromLatest(visibleByRole, 'jobStatus'), [visibleByRole]);
    const billingOpts = useMemo(() => makeOptsFromLatest(visibleByRole, 'billingStatus'), [visibleByRole]);
    const supervisedByOpts = useMemo(() => makeOptsFromAll(visibleByRole, 'supervisedBy'), [visibleByRole]);
    const verifiedByOpts = useMemo(() => makeOptsFromAll(visibleByRole, 'verifiedBy'), [visibleByRole]);
    const priorityOpts = useMemo(() => [
        { label: 'Most Priority', value: 'Most Priority' },
        { label: 'High Priority', value: 'High Priority' },
        { label: 'Medium Priority', value: 'Medium Priority' },
        { label: 'Low Priority', value: 'Low Priority' },
    ], []);
    const verifiedFlagOpts = useMemo(() => [{ label: 'Verified', value: 'Verified' }, { label: 'Not', value: 'Not' }], []);

    // ── Apply filters ─────────────────────────────────────────────────────────
    const filteredGroups = useMemo(() => {
        return visibleByRole.filter((g) => {
            const lat = g.latest;
            if (filterCallNumber.length && !filterCallNumber.includes(g.callNumber)) return false;
            if (filterPartyName.length && !g.all.some((r) => filterPartyName.includes(r.partyName))) return false;
            if (filterSupervisedBy.length && !g.all.some((r) => filterSupervisedBy.includes(r.supervisedBy))) return false;
            if (filterVerifiedBy.length && !g.all.some((r) => filterVerifiedBy.includes(r.verifiedBy))) return false;
            if (filterCallDate.length && !filterCallDate.includes(dateKey(lat.callDate, 16))) return false;
            if (filterScheduleDate.length && !filterScheduleDate.includes(dateKey(lat.scheduleDate, 16))) return false;
            if (filterTentativeDate.length && !filterTentativeDate.includes(dateKey(lat.tentativeCompletionDate, 10))) return false;
            if (filterTechnician.length && !filterTechnician.includes(lat.scheduleTechnician)) return false;
            if (filterJob.length && !filterJob.includes(lat.jobStatus)) return false;
            if (filterBilling.length && !filterBilling.includes(lat.billingStatus)) return false;
            if (filterPriority.length && !filterPriority.includes(lat.priorityCustomer)) return false;
            if (filterVerified.length && !filterVerified.includes(lat.verified || 'Not')) return false;
            if (filterDirector.length && !filterDirector.includes(lat.director || 'Not')) return false;
            return true;
        });
    }, [
        visibleByRole, filterCallNumber, filterPartyName, filterCallDate, filterScheduleDate,
        filterTentativeDate, filterTechnician, filterJob, filterBilling, filterPriority,
        filterSupervisedBy, filterVerifiedBy, filterVerified, filterDirector,
    ]);

    // ── Universal search ──────────────────────────────────────────────────────
    const searchableGroups = useMemo(() => filteredGroups.map((g) => ({
        ...g,
        _search: {
            callNumber: g.callNumber,
            partyName: g.all.map((r) => r.partyName).filter(Boolean).join(' '),
            partyAddress: g.all.map((r) => r.partyAddress).filter(Boolean).join(' '),
            callDetails: g.all.map((r) => r.callDetails).filter(Boolean).join(' '),
            technician: g.all.map((r) => r.scheduleTechnician).filter(Boolean).join(' '),
            supervisedBy: g.all.map((r) => r.supervisedBy).filter(Boolean).join(' '),
            verifiedBy: g.all.map((r) => r.verifiedBy).filter(Boolean).join(' '),
            remarks: g.all.map((r) => r.remarks).filter(Boolean).join(' '),
        },
    })), [filteredGroups]);

    const [visibleGroups, setVisibleGroups] = useState(searchableGroups);


    // ── Modal filtered related ────────────────────────────────────────────────
    const filteredRelated = useMemo(() => {
        return [...relatedRecords]
            .sort((a, b) => new Date(b.callDate || 0) - new Date(a.callDate || 0))
            .filter((r) => {
                if (modalFilterJob.length && !modalFilterJob.includes(r.jobStatus)) return false;
                if (modalFilterBilling.length && !modalFilterBilling.includes(r.billingStatus)) return false;
                return true;
            });
    }, [relatedRecords, modalFilterJob, modalFilterBilling]);

    const modalJobOpts = useMemo(() => [...new Set(relatedRecords.map((r) => r.jobStatus).filter(Boolean))].map((v) => ({ label: v, value: v })), [relatedRecords]);
    const modalBillingOpts = useMemo(() => [...new Set(relatedRecords.map((r) => r.billingStatus).filter(Boolean))].map((v) => ({ label: v, value: v })), [relatedRecords]);

    // ── Form dropdown options (mirror the excel's option lists) ────────────────
    const jobStatusDropOpts = useMemo(() => [
        { label: 'Cancel', value: 'Cancel' },
        { label: 'Complete', value: 'Complete' },
        { label: 'N/R', value: 'N/R' },
        { label: 'On Progress', value: 'On Progress' },
        { label: 'Pending', value: 'Pending' },
    ], []);
    const billingDropOpts = useMemo(() => [
        { label: 'Cancelled Job', value: 'Cancelled Job' },
        { label: 'Billed & Submitted', value: 'Billed & Submitted' },
        { label: 'Complete & Billed', value: 'Complete & Billed' },
        { label: 'Complete but not Billed', value: 'Complete but not Billed' },
        { label: 'Free Service', value: 'Free Service' },
        { label: 'N/R', value: 'N/R' },
        { label: 'On Process', value: 'On Process' },
        { label: 'Pending', value: 'Pending' },
    ], []);
    const jobReportRemarksDropOpts = useMemo(() => [
        { label: 'Complete', value: 'Complete' },
        { label: 'N/R', value: 'N/R' },
        { label: 'On Progress', value: 'On Progress' },
    ], []);
    const receiptNoteDropOpts = useMemo(() => [
        { label: 'Done', value: 'Done' },
        { label: 'N/A', value: 'N/A' },
    ], []);
    const attendDropOpts = useMemo(() => [{ label: 'Attend', value: 'Attend' }, { label: 'Not Attend', value: 'not attend' }], []);
    const priorityDropOpts = priorityOpts;

    // ── Open modal ────────────────────────────────────────────────────────────
    const openModal = async (group) => {
        const row = group.latest;
        setSelectedCallNumber(row.callNumber);
        setShowAddForm(false);
        setSaveMsg(null);
        setModalFilterJob([]);
        setModalFilterBilling([]);

        setForm({
            ...EMPTY_FORM,
            partyName: row.partyName || '',
            partyAddress: row.partyAddress || '',
            supervisedBy: row.supervisedBy || '',
            scheduleTechnician: row.scheduleTechnician || '',
            priorityCustomer: row.priorityCustomer || 'Medium Priority',
            callStatus: row.callStatus || '',
            verifiedBy: row.verifiedBy || '',
        });

        setRelatedLoading(true);
        try {
            const { data } = await api.get('/Rahul/call_register.php', { params: { callNumber: row.callNumber } });
            setRelatedRecords(data.success ? data.data : []);
        } catch { setRelatedRecords([]); }
        finally { setRelatedLoading(false); }
    };

    const closeModal = () => {
        setSelectedCallNumber(null);
        setRelatedRecords([]);
        setShowAddForm(false);
        setSaveMsg(null);
    };

    const handleChange = (e) => { const { name, value } = e.target; setForm((p) => ({ ...p, [name]: value })); };
    const handleDropChange = (name) => (value) => setForm((p) => ({ ...p, [name]: value }));

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!form.callDate || !form.callDetails) {
            setSaveMsg({ type: 'error', text: 'Call Date and Call Details are required.' });
            return;
        }
        setSaving(true);
        setSaveMsg(null);
        try {
            const payload = {
                ...form,
                callNumber: selectedCallNumber,
                callDate: form.callDate ? form.callDate.replace('T', ' ') + ':00' : null,
                scheduleDate: form.scheduleDate ? form.scheduleDate.replace('T', ' ') + ':00' : null,
                dnDate: form.dnDate ? form.dnDate + ' 00:00:00' : null,
            };
            const { data } = await api.post('/Rahul/call_register.php', payload);
            if (data.success) {
                setSaveMsg({ type: 'success', text: 'Entry added successfully.' });
                setShowAddForm(false);
                await loadAll();
                const related = await api.get('/Rahul/call_register.php', { params: { callNumber: selectedCallNumber } });
                if (related.data.success) setRelatedRecords(related.data.data);
            } else { throw new Error(data.message); }
        } catch (err) {
            setSaveMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Save failed.' });
        } finally { setSaving(false); }
    };

    const handleShowAddForm = () => {
        setShowAddForm(true);
        setSaveMsg(null);
    };

    // ── Verify actions ──────────────────────────────────────────────────────
    // "Verify": PER-ROW — the person named in that specific entry's
    // verifiedBy field verifies just that one entry (pass its slNO).
    // "Director Verify": admin/director only, ONCE per call number —
    // marks the ENTIRE call history as director-verified.
    const runVerify = async (type, slNO = null) => {
        if (!selectedCallNumber) return;
        setVerifying(true);
        setSaveMsg(null);
        try {
            const { data } = await api.post('/Rahul/call_register_verify.php', {
                callNumber: selectedCallNumber,
                type, // 'director' | 'verified'
                actorName: myName,
                slNO, // present only for per-row 'verified' actions; omitted/null for 'director'
            });
            if (data.success) {
                setSaveMsg({
                    type: 'success',
                    text: type === 'director'
                        ? 'Marked as director-verified for this call number.'
                        : 'Entry marked as verified.',
                });
                await loadAll();
                const related = await api.get('/Rahul/call_register.php', { params: { callNumber: selectedCallNumber } });
                if (related.data.success) setRelatedRecords(related.data.data);
            } else { throw new Error(data.message); }
        } catch (err) {
            setSaveMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Verify failed.' });
        } finally { setVerifying(false); }
    };

    // Group currently displayed in the modal
    const selectedGroup = useMemo(
        () => visibleByRole.find((g) => g.callNumber === selectedCallNumber) || null,
        [visibleByRole, selectedCallNumber]
    );
    const alreadyDirectorVerified = norm(selectedGroup?.latest?.director) === 'verified';

    // Per-row verify progress for the call number currently open in the modal
    const verifiedSummary = useMemo(() => {
        const total = relatedRecords.length;
        const verifiedCount = relatedRecords.filter((r) => norm(r.verified) === 'verified').length;
        return { total, verifiedCount, pending: total - verifiedCount };
    }, [relatedRecords]);
    const allRowsVerified = verifiedSummary.total > 0 && verifiedSummary.pending === 0;

    // ── Date range search (call_register_history.php) ─────────────────────
    const fetchByDateRangeHistory = useCallback(async (from_date, to_date) => {
        setRangeLoading(true);
        setError(null);
        try {
            const { data } = await api.post('/Rahul/call_register_history.php', { from_date, to_date });
            if (data.success) setRecords(data.data);
            else throw new Error(data.message || 'Failed to load records for the selected date range.');
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load records for the selected date range.');
        } finally {
            setRangeLoading(false);
        }
    }, []);

    // <DateSearch /> calls onSearch(startDate, endDate, isApplied)
    const handleDateRangeChange = (start, end, applied) => {
        setDateRange({ start, end });
        if (applied && start && end) {
            fetchByDateRangeHistory(start, end);
        } else {
            loadAll();
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">

            {/* ── Main panel ── */}
            <div
                className="bg-white w-full max-w-7xl flex flex-col rounded-2xl shadow-2xl border border-slate-200"
                style={{ height: 'calc(100vh - 2rem)', maxHeight: '92vh' }}
            >
                {/* Header */}
                <div className="flex-none flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t-2xl">
                    <div className="flex items-center gap-2">
                        <h1 className="text-sm font-bold text-slate-800">Call Register</h1>
                        {myName && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-medium">
                                {myName}{canSeeAll ? ` · ${isAdmin ? 'Admin' : isDirector ? 'Director' : 'CRM'} (full access)` : ''}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-800 transition"
                    >✕</button>
                </div>

                {/* Sub-header */}
                <div className="flex-none flex flex-col gap-0.5 px-0.5 py-0.5 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <p className="text-xs text-slate-400">
                            {loading || rangeLoading
                                ? 'Loading…'
                                : `${visibleGroups.length} of ${visibleByRole.length} call numbers · ${records.length} total entries`}
                            {dateRange.start && dateRange.end && !loading && !rangeLoading && (
                                <span className="ml-1.5 inline-flex items-center gap-1 text-blue-500 font-medium">
                                    · date range applied ({dateRange.start} → {dateRange.end})
                                </span>
                            )}
                            {' '}· click a row to view all entries &amp; add new ones
                        </p>
                        <div className="flex items-center gap-5">
                            {/* DateSearch is shared across pages — untouched; wired via its onSearch(start, end, applied) API */}
                            <DateSearch onSearch={handleDateRangeChange} />

                            <UniversalSearchBar
                                data={searchableGroups}
                                searchKeys={[
                                    '_search.callNumber', '_search.partyName', '_search.partyAddress',
                                    '_search.callDetails', '_search.technician', '_search.supervisedBy',
                                    '_search.verifiedBy', '_search.remarks',
                                ]}
                                onResults={setVisibleGroups}
                                placeholder="Search call no, company, address, details, technician, supervisor…"
                                className="max-w-md"
                            />
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="flex-none px-4 py-2 text-xs text-red-600 bg-red-50 border-b border-red-200">{error}</div>
                )}

                {/* Table area */}
                <div className="flex-1 overflow-hidden p-4">
                    <div className="h-full rounded-2xl border border-slate-200 bg-white shadow-sm flex flex-col overflow-hidden">
                        <div className="flex-1 overflow-auto">
                            <table className="w-full text-xs border-collapse" style={{ minWidth: '2050px' }}>
                                <thead className="bg-slate-100 sticky top-0 z-10">
                                    <tr>
                                        <th style={{ width: '44px' }}
                                            className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 border-b whitespace-nowrap">Sl No.</th>

                                        <th style={{ width: '150px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Date" options={callDateOpts} selectedValues={filterCallDate} onChange={setFilterCallDate} />
                                        </th>
                                        <th style={{ width: '130px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Call No." options={callNumberOpts} selectedValues={filterCallNumber} onChange={setFilterCallNumber} />
                                        </th>
                                        <th style={{ width: '155px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Client Name" options={partyNameOpts} selectedValues={filterPartyName} onChange={setFilterPartyName} />
                                        </th>
                                        <th style={{ width: '150px' }}
                                            className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 border-b whitespace-nowrap">Address</th>
                                        <th style={{ width: '145px' }}
                                            className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 border-b whitespace-nowrap">Call Details</th>
                                        <th style={{ width: '110px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Priority" options={priorityOpts} selectedValues={filterPriority} onChange={setFilterPriority} />
                                        </th>
                                        <th style={{ width: '150px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Schedule Date" options={scheduleDateOpts} selectedValues={filterScheduleDate} onChange={setFilterScheduleDate} />
                                        </th>
                                        <th style={{ width: '120px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Technician" options={technicianOpts} selectedValues={filterTechnician} onChange={setFilterTechnician} />
                                        </th>
                                        <th style={{ width: '140px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Tentative Date" options={tentativeDateOpts} selectedValues={filterTentativeDate} onChange={setFilterTentativeDate} />
                                        </th>
                                        <th style={{ width: '130px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Supervised By" options={supervisedByOpts} selectedValues={filterSupervisedBy} onChange={setFilterSupervisedBy} />
                                        </th>
                                        <th style={{ width: '110px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Job Status" options={jobOpts} selectedValues={filterJob} onChange={setFilterJob} />
                                        </th>
                                        <th style={{ width: '100px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Billing" options={billingOpts} selectedValues={filterBilling} onChange={setFilterBilling} />
                                        </th>
                                        <th style={{ width: '100px' }}
                                            className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 border-b whitespace-nowrap">Svc Charge</th>
                                        <th style={{ width: '100px' }}
                                            className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 border-b whitespace-nowrap">Net Rcvd</th>
                                        <th style={{ width: '120px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Verified By" options={verifiedByOpts} selectedValues={filterVerifiedBy} onChange={setFilterVerifiedBy} />
                                        </th>
                                        <th style={{ width: '95px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Verified" options={verifiedFlagOpts} selectedValues={filterVerified} onChange={setFilterVerified} />
                                        </th>
                                        <th style={{ width: '95px' }} className="px-2 py-2 text-left border-b">
                                            <TableHeaderFilter label="Director" options={verifiedFlagOpts} selectedValues={filterDirector} onChange={setFilterDirector} />
                                        </th>
                                        <th style={{ width: '62px' }}
                                            className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 border-b whitespace-nowrap">Entries</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading || rangeLoading ? (
                                        <SkeletonRows cols={18} rows={9} />
                                    ) : visibleGroups.length === 0 ? (
                                        <tr>
                                            <td colSpan={18} className="text-center py-10 text-slate-400">No records found.</td>
                                        </tr>
                                    ) : (
                                        visibleGroups.map((group, idx) => {
                                            const row = group.latest;
                                            const todayRow = isToday(row.callDate);
                                            return (
                                                <tr
                                                    key={group.callNumber}
                                                    onClick={() => openModal(group)}
                                                    className={`border-b cursor-pointer transition-colors ${todayRow
                                                        ? 'bg-blue-50/60 hover:bg-blue-100/70'
                                                        : 'hover:bg-slate-50'
                                                        }`}
                                                >
                                                    <td className="px-3 py-2">
                                                        <span className="flex items-center gap-1.5">
                                                            {idx + 1}
                                                            {todayRow && <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500" title="Today" />}
                                                        </span>
                                                    </td>
                                                    <td className={`px-3 py-2 whitespace-nowrap ${todayRow ? 'font-semibold text-blue-700' : ''}`}>
                                                        {fmt(row.callDate?.slice(0, 16))}
                                                    </td>
                                                    <td className="px-3 py-2 text-blue-600 font-semibold">{row.callNumber}</td>
                                                    <td className="px-3 py-2 truncate max-w-[155px]" title={row.partyName}>{fmt(row.partyName)}</td>
                                                    <td className="px-3 py-2 truncate max-w-[150px]" title={row.partyAddress}>{fmt(row.partyAddress)}</td>
                                                    <td className="px-3 py-2 truncate max-w-[145px]" title={row.callDetails}>{fmt(row.callDetails)}</td>
                                                    <td className="px-3 py-2"><Badge label={row.priorityCustomer} styleMap={PRIORITY_STYLES} /></td>
                                                    <td className="px-3 py-2 whitespace-nowrap">{fmt(row.scheduleDate?.slice(0, 16))}</td>
                                                    <td className="px-3 py-2 truncate max-w-[120px]">{fmt(row.scheduleTechnician)}</td>
                                                    <td className="px-3 py-2 whitespace-nowrap">{fmt(row.tentativeCompletionDate?.slice(0, 10))}</td>
                                                    <td className="px-3 py-2 truncate max-w-[130px]">{fmt(row.supervisedBy)}</td>
                                                    <td className="px-3 py-2"><Badge label={row.jobStatus} styleMap={JOB_STYLES} /></td>
                                                    <td className="px-3 py-2"><Badge label={row.billingStatus} styleMap={BILLING_STYLES} /></td>
                                                    <td className="px-3 py-2">{fmtAmt(row.serviceChargeAmount)}</td>
                                                    <td className="px-3 py-2">{fmtAmt(row.netAmountReceived)}</td>
                                                    <td className="px-3 py-2 truncate max-w-[120px]">{fmt(row.verifiedBy)}</td>
                                                    <td className="px-3 py-2"><Badge label={row.verified || 'Not'} styleMap={VERIFIED_STYLES} /></td>
                                                    <td className="px-3 py-2"><Badge label={row.director || 'Not'} styleMap={VERIFIED_STYLES} /></td>
                                                    <td className="px-3 py-2">
                                                        <span className={`inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded-full text-[10px] font-bold ${group.count > 1 ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'
                                                            }`}>
                                                            {group.count}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ══════════════════════ MODAL ══════════════════════ */}
            {selectedCallNumber && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl border border-slate-200 flex flex-col"
                        style={{ height: 'calc(100vh - 4rem)', maxHeight: '88vh' }}
                    >
                        {/* Modal header */}
                        <div className="flex-none flex items-center justify-between px-5 py-4 border-b border-slate-100 rounded-t-2xl">
                            <div>
                                <p className="text-sm font-bold text-slate-800 tracking-tight">
                                    Call No: <span className="text-blue-600">{selectedCallNumber}</span>
                                    {selectedGroup && (
                                        <span className="ml-2 text-xs font-normal text-slate-400">
                                            {selectedGroup.count} {selectedGroup.count === 1 ? 'entry' : 'entries'}
                                            {' · '}
                                            <span className="text-emerald-600 font-medium">{verifiedSummary.verifiedCount} verified</span>
                                            {verifiedSummary.pending > 0 && (
                                                <span className="text-amber-600 font-medium"> · {verifiedSummary.pending} pending</span>
                                            )}
                                        </span>
                                    )}
                                    {selectedGroup && (
                                        <span className="ml-2 inline-flex gap-1 align-middle">
                                            <Badge label={allRowsVerified ? 'Verified' : 'Not'} styleMap={VERIFIED_STYLES} />
                                            <Badge label={alreadyDirectorVerified ? 'Verified' : 'Not'} styleMap={VERIFIED_STYLES} />
                                        </span>
                                    )}
                                </p>
                                {selectedGroup && (
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        {selectedGroup.latest.partyName} · {selectedGroup.latest.partyAddress}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={closeModal}
                                className="w-7 h-7 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition text-lg"
                            >×</button>
                        </div>

                        {/* Modal body */}
                        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5 min-h-0">

                            {saveMsg && (
                                <div className={`flex-none px-4 py-2.5 rounded-xl text-xs font-medium border ${saveMsg.type === 'success'
                                    ? 'bg-green-50 text-green-700 border-green-200'
                                    : 'bg-red-50 text-red-600 border-red-200'
                                    }`}>{saveMsg.text}</div>
                            )}

                            {/* ── Add form ── */}
                            {showAddForm && (
                                <div className="flex-none rounded-xl border border-slate-200 bg-slate-50 p-4">
                                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-3">
                                        New Entry — {selectedCallNumber}
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                        <Field label="Call Date *">
                                            <input type="datetime-local" name="callDate" value={form.callDate} onChange={handleChange} className={inputCls} />
                                        </Field>
                                        <Field label="Client Name">
                                            <input type="text" name="partyName" value={form.partyName} onChange={handleChange} className={inputCls} placeholder="Party name" />
                                        </Field>
                                        <Field label="Company Address">
                                            <input type="text" name="partyAddress" value={form.partyAddress} onChange={handleChange} className={inputCls} placeholder="Address" />
                                        </Field>
                                        <Field label="Call Details *">
                                            <input type="text" name="callDetails" value={form.callDetails} onChange={handleChange} className={inputCls} placeholder="Details" />
                                        </Field>
                                        <Field label="Remarks">
                                            <input type="text" name="remarks" value={form.remarks} onChange={handleChange} className={inputCls} placeholder="Remarks" />
                                        </Field>
                                        <Field label="Final Review">
                                            <input type="text" name="finalReview" value={form.finalReview} onChange={handleChange} className={inputCls} placeholder="Final review" />
                                        </Field>
                                        <Field label="Status">
                                            <SearchableDropdown options={STATUS_DROPDOWN_OPTIONS} value={form.callStatus} onChange={handleDropChange('callStatus')} placeholder="Select status…" />
                                        </Field>
                                        <Field label="Job Status">
                                            <SearchableDropdown options={jobStatusDropOpts} value={form.jobStatus} onChange={handleDropChange('jobStatus')} placeholder="Select job status…" />
                                        </Field>
                                        <Field label="Priority Customer">
                                            <SearchableDropdown options={priorityDropOpts} value={form.priorityCustomer} onChange={handleDropChange('priorityCustomer')} placeholder="Select…" />
                                        </Field>
                                        <Field label="Schedule Date">
                                            <input type="datetime-local" name="scheduleDate" value={form.scheduleDate} onChange={handleChange} className={inputCls} />
                                        </Field>
                                        <Field label="Schedule Technician">
                                            <SearchableDropdown
                                                options={technicians}
                                                value={form.scheduleTechnician}
                                                onChange={handleDropChange('scheduleTechnician')}
                                                placeholder={employeesLoading ? 'Loading…' : technicians.length === 0 ? 'No technicians' : 'Select technician…'}
                                                disabled={employeesLoading}
                                            />
                                        </Field>
                                        <Field label="Tentative Completion Date">
                                            <input type="date" name="tentativeCompletionDate" value={form.tentativeCompletionDate} onChange={handleChange} className={inputCls} />
                                        </Field>
                                        <Field label="Attend / Not Attend">
                                            <SearchableDropdown options={attendDropOpts} value={form.attendStatus} onChange={handleDropChange('attendStatus')} placeholder="Select…" />
                                        </Field>
                                        <Field label="DN Date">
                                            <input type="date" name="dnDate" value={form.dnDate} onChange={handleChange} className={inputCls} />
                                        </Field>
                                        <Field label="Delivery Note No">
                                            <input type="text" name="deliveryNoteNo" value={form.deliveryNoteNo} onChange={handleChange} className={inputCls} placeholder="DN-XXX" />
                                        </Field>
                                        <Field label="Receipt Note">
                                            <SearchableDropdown options={receiptNoteDropOpts} value={form.receiptNote} onChange={handleDropChange('receiptNote')} placeholder="Select…" />
                                        </Field>
                                        <Field label="Supervised By">
                                            <SearchableDropdown
                                                options={allEmployees}
                                                value={form.supervisedBy}
                                                onChange={handleDropChange('supervisedBy')}
                                                placeholder={employeesLoading ? 'Loading…' : 'Select employee…'}
                                                disabled={employeesLoading}
                                            />
                                        </Field>
                                        <Field label="Billing Status">
                                            <SearchableDropdown options={billingDropOpts} value={form.billingStatus} onChange={handleDropChange('billingStatus')} placeholder="Select billing…" />
                                        </Field>
                                        <Field label="Job Report Remarks">
                                            <SearchableDropdown options={jobReportRemarksDropOpts} value={form.jobReportRemarks} onChange={handleDropChange('jobReportRemarks')} placeholder="Select…" />
                                        </Field>
                                        <Field label="Service Charge (₹)">
                                            <input type="number" name="serviceChargeAmount" value={form.serviceChargeAmount} onChange={handleChange} className={inputCls} placeholder="0.00" min="0" step="0.01" />
                                        </Field>
                                        <Field label="Received Amount (₹)">
                                            <input type="number" name="receivedAmount" value={form.receivedAmount} onChange={handleChange} className={inputCls} placeholder="0.00" min="0" step="0.01" />
                                        </Field>
                                        <Field label="Svc Charge Paid to Vendor (₹)">
                                            <input type="number" name="serviceChargePaid" value={form.serviceChargePaid} onChange={handleChange} className={inputCls} placeholder="0.00" min="0" step="0.01" />
                                        </Field>
                                        <Field label="Net Amount Received (₹)">
                                            <input type="number" name="netAmountReceived" value={form.netAmountReceived} onChange={handleChange} className={inputCls} placeholder="0.00" min="0" step="0.01" />
                                        </Field>
                                        <Field label="Verified Person">
                                            <SearchableDropdown
                                                options={allEmployees}
                                                value={form.verifiedBy}
                                                onChange={handleDropChange('verifiedBy')}
                                                placeholder={employeesLoading ? 'Loading…' : 'Select person to verify…'}
                                                disabled={employeesLoading}
                                            />
                                        </Field>
                                    </div>

                                    <div className="flex justify-end gap-2 mt-4">
                                        <button
                                            onClick={() => { setShowAddForm(false); setSaveMsg(null); }}
                                            className="px-4 py-1.5 text-xs rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition"
                                        >Cancel</button>
                                        <button
                                            onClick={handleSave}
                                            disabled={saving}
                                            className="px-5 py-1.5 text-xs rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-1.5"
                                        >
                                            {saving && (
                                                <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                                </svg>
                                            )}
                                            {saving ? 'Saving…' : 'Save Entry'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ── All entries for this call number ── */}
                            <div className="flex flex-col min-h-0" style={{ minHeight: '240px' }}>
                                <p className="flex-none text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-2">
                                    All entries for {selectedCallNumber}
                                    {' '}
                                    <span className="normal-case font-normal">
                                        ({filteredRelated.length}
                                        {filteredRelated.length !== relatedRecords.length ? ` of ${relatedRecords.length}` : ''})
                                    </span>
                                </p>

                                <div className="flex-1 rounded-xl border border-slate-200 overflow-auto" style={{ maxHeight: '360px' }}>
                                    <table className="w-full text-xs border-collapse" style={{ minWidth: '2700px' }}>
                                        <thead className="sticky top-0 z-20 bg-slate-100">
                                            <tr>
                                                {[
                                                    ['#', '40px'], ['Date', '140px'], ['Call No.', '110px'],
                                                    ['Company', '140px'], ['Address', '140px'], ['Details', '150px'],
                                                    ['Remarks', '160px'], ['Final Review', '140px'], ['Priority', '110px'],
                                                    ['Schedule Date', '140px'], ['Technician', '110px'], ['Tentative Completion', '130px'],
                                                    ['Attend', '90px'], ['Status', '170px'],
                                                ].map(([l, w]) => (
                                                    <th key={l} style={{ width: w }}
                                                        className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                                                        {l}
                                                    </th>
                                                ))}
                                                <th className="px-2 py-2 border-b border-slate-200">
                                                    <TableHeaderFilter label="Job" options={modalJobOpts} selectedValues={modalFilterJob} onChange={setModalFilterJob} />
                                                </th>
                                                {[
                                                    ['DN Date', '110px'], ['Delivery Note No', '130px'], ['Receipt Note', '110px'],
                                                    ['Supervised By', '120px'],
                                                ].map(([l, w]) => (
                                                    <th key={l} style={{ width: w }}
                                                        className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                                                        {l}
                                                    </th>
                                                ))}
                                                <th className="px-2 py-2 border-b border-slate-200">
                                                    <TableHeaderFilter label="Billing" options={modalBillingOpts} selectedValues={modalFilterBilling} onChange={setModalFilterBilling} />
                                                </th>
                                                {[
                                                    ['Job Report Remarks', '130px'], ['Svc Charge', '100px'], ['Received Amt', '100px'],
                                                    ['Svc Charge Paid', '110px'], ['Net Rcvd', '100px'], ['Verified Person', '120px'],
                                                    ['Verified', '90px'], ['Director', '90px'],
                                                ].map(([l, w]) => (
                                                    <th key={l} style={{ width: w }}
                                                        className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                                                        {l}
                                                    </th>
                                                ))}
                                                <th style={{ width: '130px' }}
                                                    className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">
                                                    Verify Action
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {relatedLoading ? (
                                                <SkeletonRows cols={29} rows={3} />
                                            ) : filteredRelated.length === 0 ? (
                                                <tr>
                                                    <td colSpan={29} className="px-4 py-8 text-center text-slate-400 text-xs">No entries found.</td>
                                                </tr>
                                            ) : (
                                                filteredRelated.map((r, idx) => {
                                                    const todayRow = isToday(r.callDate);
                                                    const isLatest = idx === 0;
                                                    return (
                                                        <tr key={r.slNO} className={`border-b border-slate-100 transition-colors ${isLatest
                                                            ? todayRow ? 'bg-blue-50/80' : 'bg-slate-50/80'
                                                            : todayRow ? 'bg-blue-50/30 hover:bg-blue-100/40' : 'hover:bg-slate-50'
                                                            }`}>
                                                            <td className="px-3 py-2 text-slate-400">
                                                                <span className="flex items-center gap-1">
                                                                    {r.slNO}
                                                                    {isLatest && (
                                                                        <span className="text-[8px] font-bold text-blue-500 uppercase tracking-wide">latest</span>
                                                                    )}
                                                                    {todayRow && !isLatest && (
                                                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />
                                                                    )}
                                                                </span>
                                                            </td>
                                                            <td className={`px-3 py-2 whitespace-nowrap ${todayRow ? 'font-semibold text-blue-700' : 'text-slate-600'}`}>
                                                                {fmt(r.callDate?.slice(0, 16))}
                                                            </td>
                                                            <td className="px-3 py-2 font-semibold text-blue-600">{fmt(r.callNumber)}</td>
                                                            <td className="px-3 py-2 text-slate-700 max-w-[140px] truncate" title={r.partyName}>{fmt(r.partyName)}</td>
                                                            <td className="px-3 py-2 text-slate-500 max-w-[140px] truncate" title={r.partyAddress}>{fmt(r.partyAddress)}</td>
                                                            <td className="px-3 py-2 text-slate-600 max-w-[150px] truncate" title={r.callDetails}>{fmt(r.callDetails)}</td>
                                                            <td className="px-3 py-2 text-slate-400 max-w-[160px] truncate" title={r.remarks}>{fmt(r.remarks)}</td>
                                                            <td className="px-3 py-2 text-slate-400 max-w-[140px] truncate" title={r.finalReview}>{fmt(r.finalReview)}</td>
                                                            <td className="px-3 py-2"><Badge label={r.priorityCustomer} styleMap={PRIORITY_STYLES} /></td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-slate-500">{fmt(r.scheduleDate?.slice(0, 16))}</td>
                                                            <td className="px-3 py-2 text-slate-500 max-w-[110px] truncate">{fmt(r.scheduleTechnician)}</td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-slate-500">{fmt(r.tentativeCompletionDate?.slice(0, 10))}</td>
                                                            <td className="px-3 py-2"><Badge label={r.attendStatus} styleMap={ATTEND_STYLES} /></td>
                                                            <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmt(r.callStatus)}</td>
                                                            <td className="px-3 py-2"><Badge label={r.jobStatus} styleMap={JOB_STYLES} /></td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-slate-500">{fmt(r.dnDate?.slice(0, 10))}</td>
                                                            <td className="px-3 py-2 text-slate-500 max-w-[130px] truncate">{fmt(r.deliveryNoteNo)}</td>
                                                            <td className="px-3 py-2"><Badge label={r.receiptNote} styleMap={RECEIPT_STYLES} /></td>
                                                            <td className="px-3 py-2 text-slate-500 max-w-[120px] truncate">{fmt(r.supervisedBy)}</td>
                                                            <td className="px-3 py-2"><Badge label={r.billingStatus} styleMap={BILLING_STYLES} /></td>
                                                            <td className="px-3 py-2"><Badge label={r.jobReportRemarks} styleMap={JOB_REPORT_STYLES} /></td>
                                                            <td className="px-3 py-2 font-medium text-slate-700">{fmtAmt(r.serviceChargeAmount)}</td>
                                                            <td className="px-3 py-2 font-medium text-slate-700">{fmtAmt(r.receivedAmount)}</td>
                                                            <td className="px-3 py-2 font-medium text-slate-700">{fmtAmt(r.serviceChargePaid)}</td>
                                                            <td className="px-3 py-2 font-medium text-slate-700">{fmtAmt(r.netAmountReceived)}</td>
                                                            <td className="px-3 py-2 text-slate-500 max-w-[120px] truncate">{fmt(r.verifiedBy)}</td>
                                                            <td className="px-3 py-2"><Badge label={r.verified || 'Not'} styleMap={VERIFIED_STYLES} /></td>
                                                            <td className="px-3 py-2"><Badge label={r.director || 'Not'} styleMap={VERIFIED_STYLES} /></td>
                                                            <td className="px-3 py-2">
                                                                {norm(r.verified) === 'verified' ? (
                                                                    <span className="text-[10px] text-green-600 font-semibold">✓ Verified</span>
                                                                ) : myName && norm(r.verifiedBy) === norm(myName) ? (
                                                                    <button
                                                                        onClick={() => runVerify('verified', r.slNO)}
                                                                        disabled={verifying}
                                                                        className="px-2.5 py-1 text-[10px] rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
                                                                    >
                                                                        {verifying ? '…' : 'Verify'}
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-[10px] text-slate-300">—</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Modal footer */}
                        <div className="flex-none flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
                            <span className="text-xs text-slate-400">
                                {filteredRelated.length} entr{filteredRelated.length !== 1 ? 'ies' : 'y'} for {selectedCallNumber}
                            </span>
                            <div className="flex gap-2">
                                {/* "Director Verify" — admin / director only, one-time for the whole call number */}
                                {canDirectorVerify && !alreadyDirectorVerified && (
                                    <button
                                        onClick={() => runVerify('director')}
                                        disabled={verifying}
                                        className="px-4 py-1.5 text-xs rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
                                    >{verifying ? 'Verifying…' : 'Director Verify'}</button>
                                )}
                                <button
                                    onClick={closeModal}
                                    className="px-4 py-1.5 text-xs rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 transition"
                                >Close</button>
                                {!showAddForm && (
                                    <button
                                        onClick={handleShowAddForm}
                                        className="px-4 py-1.5 text-xs rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition flex items-center gap-1.5"
                                    >
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                        </svg>
                                        Add Entry
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}